import { ApiError, authApi } from '@/lib/api';
import { authStore } from '@/lib/auth-store';
import { setLaunchFailure } from '@/lib/launch-failure';
import { queryClient } from '@/lib/query-client';
import { clearPlatformLock, isPlatformLocked, setPlatformLock } from '@/lib/platform-lock';

/**
 * Portal redirects stay on this application unless the value is an absolute
 * platform URL (unified-access.md §3 step 5, e.g. a platform's own
 * `http://localhost:5180/...` sending the browser here for a session).
 * Internal application paths are revalidated by the API's own routing.
 * Absolute values only pass a shape check here (http/https, no control
 * characters) — they are NOT trusted yet. The actual open-redirect
 * protection happens server-side in `POST /launch/return`
 * (`authApi.launchReturn`, see `resolvePortalReturn` below): the browser is
 * only ever navigated to the launch URL that endpoint returns, never to this
 * raw string, so an origin that does not belong to an active platform can
 * never be reached through this value.
 */
export function safePortalReturn(value: unknown): string {
  if (typeof value !== 'string' || /[\\\r\n]/.test(value)) return '/';

  if (value.startsWith('/')) {
    if (value.startsWith('//')) return '/';
    try {
      const target = new URL(value, 'https://portal.invalid');
      return target.origin === 'https://portal.invalid' ? target.pathname + target.search : '/';
    } catch {
      return '/';
    }
  }

  try {
    const target = new URL(value);
    return target.protocol === 'http:' || target.protocol === 'https:' ? target.toString() : '/';
  } catch {
    return '/';
  }
}

/** True for a `safePortalReturn` value that is a platform's absolute URL rather than an internal path. */
export function isAbsolutePortalReturn(value: string): boolean {
  return value !== '/' && !value.startsWith('/');
}

/**
 * Starts (or retargets) single-platform sign-in from a URL's query, e.g.
 * `/login?lock=1&returnTo=https://pda.example/` (unified-access.md section
 * 3.2). Only an absolute platform address can lock; anything else leaves the
 * current state unchanged, so a plain Unified Access link behaves as before.
 */
export function capturePlatformLock(search: Record<string, unknown>): void {
  const lock = search['lock'];
  if (lock !== '1' && lock !== 1 && lock !== true) return;
  const returnTo = safePortalReturn(search['returnTo']);
  if (isAbsolutePortalReturn(returnTo)) setPlatformLock(returnTo);
}

export type PortalNavigate = (opts: { to: '/' | '/login' }) => unknown;

/**
 * Locked sign-in never falls back to the portal (unified-access.md section
 * 3.2): the employee who may not open the locked platform is signed out of
 * Unified Access, so another account can sign in at once, and the login form
 * shows the translated reason. Returns the reason for a caller that is the
 * login form itself.
 */
export async function refuseLockedSignIn(
  error: unknown,
  navigate: PortalNavigate,
): Promise<unknown> {
  setLaunchFailure(error);
  try {
    await authApi.logout();
  } catch {
    // An already-ended session is the goal; the form is shown either way.
  }
  authStore.clear();
  queryClient.clear();
  void navigate({ to: '/login' });
  return error;
}

/** `POST /launch/return` did not recognise the address as a registered platform. */
function isUnknownPlatform(error: unknown): boolean {
  return error instanceof ApiError && error.code === 'NOT_FOUND';
}

/**
 * Sends the browser to `returnTo` after login and branch selection
 * (unified-access.md §3 step 5). An internal path is a plain assignment
 * (routes.ts revalidates it); an absolute platform URL is always resolved
 * through `POST /launch/return` first and navigation only ever targets the
 * launch URL that call returns — never the raw `returnTo` — so an origin
 * that is not an active platform can never be reached this way. A rejected
 * or failed resolution falls back to the portal root, which shows the reason,
 * instead of leaving the employee stuck.
 *
 * In locked sign-in (section 3.2) a refusal signs the employee out and
 * returns to the login form instead (`refuseLockedSignIn`); the reason is
 * resolved with the promise. An address the server does not know as a
 * platform drops the lock and behaves as an unlocked sign-in, so a crafted
 * `lock=1` link can neither redirect elsewhere nor keep a tab locked.
 */
export async function goToPortalReturn(
  returnTo: string | undefined,
  navigate: PortalNavigate,
): Promise<unknown> {
  if (!returnTo || returnTo === '/') {
    void navigate({ to: '/' });
    return undefined;
  }
  if (!isAbsolutePortalReturn(returnTo)) {
    globalThis.location.assign(returnTo);
    return undefined;
  }
  try {
    const { url } = await authApi.launchReturn(returnTo);
    globalThis.location.href = url;
    return undefined;
  } catch (error) {
    if (isPlatformLocked()) {
      if (!isUnknownPlatform(error)) return refuseLockedSignIn(error, navigate);
      clearPlatformLock();
    }
    // The launcher shows the translated reason (e.g. NO_PLATFORM_ACCESS).
    setLaunchFailure(error);
    void navigate({ to: '/' });
    return undefined;
  }
}
